/**
 * Global-search helpers: menu gating, result mapping, and pagination.
 */
import * as Icons from 'lucide-react';
import { SEARCH_MENUS } from '@/constants/search-menus.constants';
import type { InterfaceElement } from '@/types/interface-elements.types';
import type {
  GlobalSearchResults,
  MenuSearchResult,
  SearchResultItem,
  SearchResultType,
  SearchRole,
  UserSearchResult,
} from '@/types/search.types';
import {
  DYNAMIC_MENUS_TO_EXCLUDE,
  REMOTE_ONLY_MENU_IDS,
  ADMINS_GENERIC_CREATE_MENU,
  ADMINS_GENERIC_VIEW_MENU,
  ROLES_MANAGEMENT_MENUS,
  TUTORS_GENERIC_CREATE_MENU,
  TUTORS_GENERIC_VIEW_MENU,
  VOLUNTEERS_GENERIC_CREATE_MENU,
  VOLUNTEERS_GENERIC_VIEW_MENU,
  SEARCH_ESTABLISHMENT_SUBLABEL,
  SEARCH_GROUP_RESOURCES,
  SEARCH_MENU_COLOR_CYCLE,
  SEARCH_MENU_SUBLABEL,
  SEARCH_MIN_QUERY_LENGTH,
  SEARCH_PAGE_SIZE,
  SEARCH_RESULT_ICON_COLOR_MAP,
  SEARCH_RESULT_ICON_MAP,
} from '@/components/search/search-configs';

/** Store slice used to gate create / tab menus. */
type SearchElementsByInterface = Record<string, InterfaceElement[]>;

/** Inputs for {@link buildDynamicSearchMenus}. */
export interface BuildDynamicSearchMenusParams {
  mode: 'in_person' | 'remote' | null;
  sidebarElements: InterfaceElement[];
  adminsElements: InterfaceElement[];
  tutorsElements: InterfaceElement[];
  volunteersElements: InterfaceElement[];
  sessionsElements: InterfaceElement[];
  reportsElements: InterfaceElement[];
}

/** Inputs for {@link generateRemoteTabMenus}. */
interface RemoteTabMenusParams {
  elements: InterfaceElement[];
  interfaceName: string;
  route: string;
  icon: string;
  baseLabel: string;
  keywords: string[];
}

/** Strip accents and lowercase for search matching. */
function normalizeForSearch(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** True when the user may see a create-menu entry (write / create action present). */
function canAccessCreateMenu(
  menu: MenuSearchResult,
  elementsByInterface: SearchElementsByInterface,
): boolean {
  if (!menu.requiresWrite && menu.type !== 'create') return true;
  const resourceElements = elementsByInterface[menu.resource];
  if (!resourceElements?.length) return true;
  return resourceElements.some(
    (element) =>
      element.elementId === 'action-create' ||
      element.elementId.startsWith('action-create-') ||
      element.config?.subtype === 'create-role',
  );
}

/** True when the menu's required interface element is present. */
function hasRequiredMenuElement(
  menu: MenuSearchResult,
  elementsByInterface: SearchElementsByInterface,
): boolean {
  if (!menu.requiredElement) return true;
  const storeElements = elementsByInterface[menu.requiredElement.interface];
  return (storeElements ?? []).some(
    (element) => element.elementId === menu.requiredElement?.elementId,
  );
}

/** Hide menus that only apply when several admin tabs are visible. */
function isExcludedAsSingleAdminTab(
  menu: MenuSearchResult,
  elementsByInterface: SearchElementsByInterface,
): boolean {
  if (!menu.excludeIfSingleAdminTab) return false;
  const resourceElements = elementsByInterface[menu.resource];
  if (!resourceElements) return false;
  return resourceElements.filter((element) => element.elementType === 'tab').length <= 1;
}

/** True when the menu label or keywords contain the normalized query. */
function menuMatchesQuery(menu: MenuSearchResult, normalizedQuery: string): boolean {
  const matchesKeywords = menu.keywords.some((keyword) =>
    normalizeForSearch(keyword).includes(normalizedQuery),
  );
  return matchesKeywords || normalizeForSearch(menu.label).includes(normalizedQuery);
}

/** Collect sidebar `menu-*` resources the current user can open. */
function collectAccessibleSearchResources(sidebarMenus: InterfaceElement[]): Set<string> {
  const accessibleResources = new Set<string>();
  for (const element of sidebarMenus) {
    if (!element.elementId.startsWith('menu-')) continue;
    accessibleResources.add(element.elementId.replace('menu-', ''));
  }
  return accessibleResources;
}

/** Local menu hits for the current query, gated by sidebar RBAC. */
export function filterSearchMenus(
  menus: MenuSearchResult[],
  query: string,
  elementsByInterface: SearchElementsByInterface,
): MenuSearchResult[] {
  if (!query || query.length < SEARCH_MIN_QUERY_LENGTH) return [];

  const normalizedQuery = normalizeForSearch(query);
  const sidebarMenus = elementsByInterface.sidebar ?? [];
  const accessibleResources = collectAccessibleSearchResources(sidebarMenus);

  return menus.filter((menu) => {
    if (menu.hidden) return false;
    const hasAccess =
      accessibleResources.has(menu.resource) ||
      (elementsByInterface[menu.resource]?.length ?? 0) > 0;
    if (!hasAccess) return false;
    if (!canAccessCreateMenu(menu, elementsByInterface)) return false;
    if (!hasRequiredMenuElement(menu, elementsByInterface)) return false;
    if (isExcludedAsSingleAdminTab(menu, elementsByInterface)) return false;
    return menuMatchesQuery(menu, normalizedQuery);
  });
}

/** Roles for search chips: API array, else the bucket's single role. */
function searchHitRoles(user: UserSearchResult): SearchRole[] | undefined {
  if (user.roles && user.roles.length > 0) return user.roles;
  if (!user.roleCode) return undefined;
  return [{ code: user.roleCode, label: user.roleLabel || user.roleCode }];
}

/** Full name shown on a user search hit. */
function userSearchLabel(user: UserSearchResult): string {
  return `${user.firstName} ${user.lastName}`;
}

/** Student label: display name, else first + last. */
function studentSearchLabel(user: UserSearchResult): string {
  return user.displayName || `${user.firstName} ${user.lastName}`.trim();
}

/** Map one user bucket to command-palette rows. */
function mapUserSearchHits(
  users: UserSearchResult[] | undefined,
  type: SearchResultType,
  routeFor: (user: UserSearchResult) => string,
  labelFor: (user: UserSearchResult) => string,
): SearchResultItem[] {
  if (!users?.length) return [];
  return users.map((user) => ({
    id: user.id,
    type,
    label: labelFor(user),
    sublabel: user.roleLabel,
    roles: searchHitRoles(user),
    icon: SEARCH_RESULT_ICON_MAP[type],
    iconColor: SEARCH_RESULT_ICON_COLOR_MAP[type],
    route: routeFor(user),
    data: user,
  }));
}

/** Map API entity buckets to command-palette rows. */
export function mapSearchDataResults(dataResults: GlobalSearchResults | null): SearchResultItem[] {
  if (!dataResults) return [];

  const establishments = dataResults.establishments?.data ?? [];
  const establishmentRows: SearchResultItem[] = establishments.map((establishment) => ({
    id: establishment.id,
    type: 'establishment',
    label: establishment.name,
    sublabel: SEARCH_ESTABLISHMENT_SUBLABEL,
    icon: SEARCH_RESULT_ICON_MAP.establishment,
    iconColor: SEARCH_RESULT_ICON_COLOR_MAP.establishment,
    route: `/establishments?view=${establishment.id}`,
    data: establishment,
  }));

  return [
    ...mapUserSearchHits(
      dataResults.tutors?.data,
      'tutor',
      (user) => `/tutors?view=${user.id}`,
      userSearchLabel,
    ),
    ...mapUserSearchHits(
      dataResults.students?.data,
      'student',
      (user) => `/students?view=${user.id}`,
      studentSearchLabel,
    ),
    ...mapUserSearchHits(
      dataResults.volunteers?.data,
      'volunteer',
      (user) => `/volunteers?view=${user.id}`,
      userSearchLabel,
    ),
    ...mapUserSearchHits(
      dataResults.admins?.data,
      'admin',
      (user) => `/admins?tab=${user.roleCode.toLowerCase()}&view=${user.id}`,
      userSearchLabel,
    ),
    ...establishmentRows,
  ];
}

/** Map local menu hits to command-palette rows. */
export function mapSearchMenuResults(menuResults: MenuSearchResult[]): SearchResultItem[] {
  return menuResults.map((menu, index) => ({
    id: menu.id,
    type: 'menu' as const,
    label: menu.label,
    sublabel: menu.type === 'create' ? SEARCH_MENU_SUBLABEL.create : SEARCH_MENU_SUBLABEL.view,
    icon: menu.icon,
    iconColor: SEARCH_MENU_COLOR_CYCLE[index % SEARCH_MENU_COLOR_CYCLE.length],
    route: menu.route,
  }));
}

/** One page of menu rows. */
export function paginateSearchResults<T>(
  items: T[],
  page: number,
  pageSize = SEARCH_PAGE_SIZE,
): T[] {
  const startIndex = (page - 1) * pageSize;
  return items.slice(startIndex, startIndex + pageSize);
}

/** Visible range label (`1-5 sur 12`). */
export function formatSearchPageRange(
  page: number,
  total: number,
  pageSize = SEARCH_PAGE_SIZE,
): string {
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return `${start}-${end} sur ${total}`;
}

/** True when the previous page button is enabled. */
export function canGoToPrevSearchPage(page: number): boolean {
  return page > 1;
}

/** True when the next page button is enabled. */
export function canGoToNextSearchPage(
  page: number,
  total: number,
  pageSize = SEARCH_PAGE_SIZE,
): boolean {
  return page * pageSize < total;
}

/** Move the keyboard highlight down; stay on the last row. */
export function nextSearchActiveIndex(current: number, length: number): number {
  return current < length - 1 ? current + 1 : current;
}

/** Move the keyboard highlight up; `-1` when leaving the first row. */
export function prevSearchActiveIndex(current: number): number {
  return current > 0 ? current - 1 : -1;
}

/** Sidebar group whose route matches the search hit (opens that group). */
export function findSidebarGroupForRoute(
  route: string,
  sidebarElements: InterfaceElement[],
): InterfaceElement | undefined {
  const destPath = route.split('?')[0];
  return sidebarElements.find((element) => {
    const rawPath = element.config?.routePath;
    const elementPath = typeof rawPath === 'string' ? rawPath.split('?')[0] : undefined;
    return elementPath === destPath && !!element.groupLabel;
  });
}

/** Lucide component taken from the namespace map. */
type SearchLucideIcon = typeof Icons.Search;

/** Lucide icon for a search row; falls back to Search. */
export function resolveSearchResultIcon(name: string): SearchLucideIcon {
  const iconsMap = Icons as unknown as Record<string, SearchLucideIcon>;
  return iconsMap[name] || Icons.Search;
}

/** Role / tab code from an interface-element config or id. */
function searchElementCode(element: InterfaceElement, prefix: string): string {
  const targetRole = element.config?.targetRole;
  if (typeof targetRole === 'string' && targetRole) return targetRole;
  return element.elementId.replace(prefix, '').toLowerCase();
}

/** View menus for each tab of an interface. */
function generateTabMenus(
  elements: InterfaceElement[],
  interfaceName: string,
  defaultIcon: string,
): MenuSearchResult[] {
  return elements
    .filter((element) => element.elementType === 'tab')
    .map((tab) => {
      const code = searchElementCode(tab, 'tab-');
      return {
        id: `menu-${interfaceName}-view-${code}`,
        label: `Consulter les ${tab.elementLabel}`,
        type: 'view' as const,
        route: `/${interfaceName}?tab=${code}`,
        icon: defaultIcon,
        keywords: [code, tab.elementLabel.toLowerCase(), 'consulter', 'voir', interfaceName],
        resource: interfaceName,
        requiredElement: { interface: interfaceName, elementId: tab.elementId },
      };
    });
}

/** Create menus for each `option-create-*` element. */
function generateCreateMenus(
  elements: InterfaceElement[],
  interfaceName: string,
): MenuSearchResult[] {
  return elements
    .filter((element) => element.elementId.startsWith('option-create-'))
    .map((option) => {
      const code = searchElementCode(option, 'option-create-');
      return {
        id: `menu-${interfaceName}-create-${code}`,
        label: option.elementLabel.startsWith('Créer')
          ? option.elementLabel
          : `Créer un ${option.elementLabel}`,
        type: 'create' as const,
        route: `/${interfaceName}?create=true&role=${code}`,
        icon: 'UserPlus',
        keywords: [code, option.elementLabel.toLowerCase(), 'créer', 'ajouter', 'nouveau'],
        resource: interfaceName,
        requiresWrite: true,
        requiredElement: { interface: interfaceName, elementId: option.elementId },
      };
    });
}

/** Remote-mode tab menus with a shared base label. */
function generateRemoteTabMenus(params: RemoteTabMenusParams): MenuSearchResult[] {
  return params.elements
    .filter((element) => element.elementType === 'tab')
    .map((tab) => {
      const code = searchElementCode(tab, 'tab-');
      return {
        id: `menu-${params.interfaceName}-view-${code}`,
        label: `${params.baseLabel} — ${tab.elementLabel}`,
        type: 'view' as const,
        route: `${params.route}?tab=${code}`,
        icon: params.icon,
        keywords: [...params.keywords, code, tab.elementLabel.toLowerCase()],
        resource: params.interfaceName,
        requiredElement: { interface: params.interfaceName, elementId: tab.elementId },
      };
    });
}

/** Generic view + create rows when an interface has at least two tabs / create options. */
function genericGroupMenus(
  tabMenus: MenuSearchResult[],
  createMenus: MenuSearchResult[],
  viewMenu: MenuSearchResult,
  createMenu: MenuSearchResult,
): MenuSearchResult[] {
  const menus: MenuSearchResult[] = [];
  if (tabMenus.length >= 2) menus.push(viewMenu);
  if (createMenus.length >= 2) menus.push(createMenu);
  return menus;
}

/** Sidebar view rows for resources that are not already in the static catalog. */
function buildSidebarSearchMenus(
  sidebarElements: InterfaceElement[],
  existingMenuResources: Set<string>,
): MenuSearchResult[] {
  return sidebarElements
    .filter((element) => {
      if (element.elementType !== 'menu' || !element.elementId.startsWith('menu-')) return false;
      const resource = element.elementId.replace('menu-', '');
      return !existingMenuResources.has(resource) && !SEARCH_GROUP_RESOURCES.has(resource);
    })
    .map((menu): MenuSearchResult => {
      const resource = menu.elementId.replace('menu-', '');
      const routePath = menu.config?.routePath;
      return {
        id: `global-search-${menu.elementId}`,
        label: `Consulter ${menu.elementLabel.toLowerCase()}`,
        type: 'view',
        route: typeof routePath === 'string' && routePath ? routePath : `/${resource}`,
        icon: 'Users',
        keywords: [resource, menu.elementLabel.toLowerCase(), 'consulter', 'voir'],
        resource,
      };
    });
}

/** Command-palette menus from loaded interface elements and current mode. */
export function buildDynamicSearchMenus(params: BuildDynamicSearchMenusParams): MenuSearchResult[] {
  const baseMenus = SEARCH_MENUS.filter((menu) => !DYNAMIC_MENUS_TO_EXCLUDE.has(menu.id));
  const existingMenuResources = new Set(
    baseMenus.filter((menu) => menu.type === 'view').map((menu) => menu.resource),
  );

  const adminsTabMenus = generateTabMenus(params.adminsElements, 'admins', 'Shield');
  const adminsCreateMenus = generateCreateMenus(params.adminsElements, 'admins');
  const tutorsTabMenus = generateTabMenus(params.tutorsElements, 'tutors', 'GraduationCap');
  const tutorsCreateMenus = generateCreateMenus(params.tutorsElements, 'tutors');
  const volunteersTabMenus = generateTabMenus(params.volunteersElements, 'volunteers', 'Heart');
  const volunteersCreateMenus = generateCreateMenus(params.volunteersElements, 'volunteers');

  const allMenus = [
    ...baseMenus,
    ...buildSidebarSearchMenus(params.sidebarElements, existingMenuResources),
    ...genericGroupMenus(
      adminsTabMenus,
      adminsCreateMenus,
      ADMINS_GENERIC_VIEW_MENU,
      ADMINS_GENERIC_CREATE_MENU,
    ),
    ...adminsTabMenus,
    ...adminsCreateMenus,
    ...genericGroupMenus(
      tutorsTabMenus,
      tutorsCreateMenus,
      TUTORS_GENERIC_VIEW_MENU,
      TUTORS_GENERIC_CREATE_MENU,
    ),
    ...tutorsTabMenus,
    ...tutorsCreateMenus,
    ...genericGroupMenus(
      volunteersTabMenus,
      volunteersCreateMenus,
      VOLUNTEERS_GENERIC_VIEW_MENU,
      VOLUNTEERS_GENERIC_CREATE_MENU,
    ),
    ...volunteersTabMenus,
    ...volunteersCreateMenus,
    ...generateRemoteTabMenus({
      elements: params.sessionsElements,
      interfaceName: 'sessions',
      route: '/sessions',
      icon: 'Calendar',
      baseLabel: 'Séances',
      keywords: ['séances', 'sessions', 'distanciel', 'remote'],
    }),
    ...generateRemoteTabMenus({
      elements: params.reportsElements,
      interfaceName: 'reports',
      route: '/reports',
      icon: 'FileText',
      baseLabel: 'Comptes rendus',
      keywords: ['comptes', 'rendus', 'cr', 'reports', 'scoring', 'distanciel'],
    }),
    ...ROLES_MANAGEMENT_MENUS,
  ];

  const isRemote = params.mode === 'remote';
  return allMenus.filter((menu) => !REMOTE_ONLY_MENU_IDS.has(menu.id) || isRemote);
}
